import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import './style.scss';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultButton from 'components/Utils/NewDefaultButton';
import AddressFields from 'components/Patient/EditAddress/AddressFields';
import ClinicHours from 'components/Clinic/ClinicHours';
import PerformanceUtils from 'services/PerformanceUtils';
import Collapse from 'components/Utils/Collapse';
import ClinicUtils from 'services/ClinicUtils';
import StackItemsView from 'components/Common/StackItemsView';
import Icon from 'components/Utils/NewIcon';
import IconButton from '@material-ui/core/IconButton';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import UIUtils, { DefaultSwitch } from 'services/UIUtils';
import MenuItem from '@material-ui/core/MenuItem';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import PhysicianUtils from 'services/PhysicianUtils';
import { useAppSelector } from 'careand-redux/store';
import { PhysicianPhysicianTypeEnum } from 'generated/api';
import StaffUtils from 'services/StaffUtils';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';

const INDEXES = ['clinic', 'home', 'video', 'lab'];
const DEFAULT_VALUES = [
  { shut: true },
  { shut: true },
  { shut: true },
  { shut: true },
  { shut: true },
  { shut: true },
  { shut: true },
];

function prepareClinic(clinic = {}) {
  let result = PerformanceUtils.clone(clinic);
  if (!result.hours) {
    result.hours = {};
  }
  INDEXES.forEach((index) => {
    if (!result.hours[index]) result.hours[index] = DEFAULT_VALUES;
  });
  return result;
}

const ClinicDetails = (props) => {
  const dispatch = useAppDispatch();

  const [clinic, setClinic] = useState(prepareClinic(props.clinic));
  console.log(clinic);
  const [collapsed, setCollapsed] = useState(true);
  const [savingClinic, setSavingClinic] = useState(false);

  const physicians = useAppSelector((state) => state.physicians.byId);
  const settings = useAppSelector((state) => state.authentication.settings);
  const sesEnabled = !!settings?.modules?.ses?.enabled;
  const sesDomain = settings?.ses_domain;
  const faxEnabled = !!settings?.modules?.fax?.enabled;

  const missingFields = !clinic.address?.latitude || !clinic.address?.longitude;

  const saveClinicDisabled = PerformanceUtils.isEqual(clinic, props.clinic) || missingFields;

  const allPhysicians = useMemo(() => {
    return StaffUtils.filterActiveUsers(Object.values(physicians)).filter(
      ({ physician_type, intern_of }) =>
        !intern_of &&
        (!physician_type || physician_type === PhysicianPhysicianTypeEnum.NursePractitioner),
    );
  }, [physicians]);

  async function saveClinic(event) {
    event.preventDefault();
    event.stopPropagation();
    if (saveClinicDisabled) return;

    const clinicInfo = {
      ...clinic,
      hours: ClinicUtils.prepareHours(clinic.hours),
    };
    setSavingClinic(true);
    await dispatch(api.Resource.setClinicInfo({ clinicInfo }), {
      errorTitle: 'Clinic Info',
    });
    setSavingClinic(false);
  }

  function onClinicChange(event) {
    let name = event.target.name;
    let value = event.target.value;
    setClinic({ ...clinic, [name]: value });
  }

  function onAddressChange(event) {
    let { name, value } = event.target;
    let address = { ...(clinic.address || {}), [name]: value };
    setClinic({ ...clinic, address });
  }

  function setAddress(address) {
    setClinic({ ...clinic, address });
  }

  function onConfigurationChange(event) {
    let { name, value } = event.target;
    let clinic_configuration = { ...(clinic.clinic_configuration || {}), [name]: value };
    setClinic({ ...clinic, clinic_configuration });
  }

  async function toggleCollapsed() {
    setCollapsed(!collapsed);
  }

  return (
    <div className="details-panel clinic-details scroll-list">
      <form style={{ width: '100%', maxWidth: '800px' }} onSubmit={saveClinic}>
        {/* Basic Information Section */}
        <div className="clinic-section">
          <div className="section-header">
            <Icon src="hospital" height="20px" className="section-icon" />
            <span className="section-title">Basic Information</span>
          </div>
          <div className="section-content">
            <DefaultTextField
              name="name"
              label="Clinic Name"
              value={clinic.name}
              onChange={onClinicChange}
            />
            <div className="field-row">
              <DefaultTextField
                name="phone"
                label="Phone"
                value={clinic.phone}
                onChange={onClinicChange}
              />
              <DefaultTextField
                name="fax"
                label="Fax"
                value={clinic.fax}
                onChange={onClinicChange}
              />
            </div>
            <div className="field-row">
              <DefaultTextField
                name="email"
                label="Email"
                value={clinic.email}
                onChange={onClinicChange}
              />
              <DefaultTextField
                name="website"
                label="Website"
                value={clinic.website}
                onChange={onClinicChange}
              />
            </div>
            {sesEnabled && sesDomain && (
              <div className="email-prefix-field">
                <DefaultTextField
                  name="email_prefix"
                  label="Results Email Prefix"
                  value={clinic.email_prefix || ''}
                  onChange={onClinicChange}
                />
                <span className="email-preview">
                  {clinic.email_prefix || 'results'}.{clinic.tenant_id}@{sesDomain}
                </span>
                <span className="field-helper-text">
                  Emails sent to this address will appear as documents in this clinic's inbox
                </span>
              </div>
            )}
            <div className="field-row">
              <DefaultTextField
                name="google_url"
                label="Google My Business URL"
                value={clinic.google_url}
                onChange={onClinicChange}
              />
              <DefaultTextField
                name="google_maps_url"
                label="Google Maps URL"
                value={clinic.google_maps_url}
                onChange={onClinicChange}
              />
            </div>
            <div className="field-row">
              <DefaultDatePicker
                name="date_start"
                label="Date Started"
                value={clinic.date_start}
                onChange={(e, value) => onClinicChange({ target: { name: 'date_start', value } })}
              />
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <DefaultSwitch
                  name="hide"
                  className="red"
                  padded={false}
                  onChange={(event, isInputChecked) => {
                    setClinic({ ...clinic, hide: isInputChecked });
                  }}
                  label="Hide From Patients"
                  checked={!!clinic.hide}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Address Section */}
        <div className="clinic-section">
          <div className="section-header">
            <Icon src="location-dot" height="20px" className="section-icon" />
            <span className="section-title">Address</span>
          </div>
          <div className="section-content">
            <AddressFields
              address={clinic.address || {}}
              onChange={onAddressChange}
              onSearchResult={setAddress}
              style={{ width: '100%', marginRight: 0 }}
              includeLatLng={true}
            />
          </div>
        </div>

        {/* Clinical Director Section */}
        <div className="clinic-section">
          <div className="section-header">
            <Icon src="user-nurse" height="20px" className="section-icon" />
            <span className="section-title">Clinical Director</span>
          </div>
          <div className="section-content">
            <DefaultSelectField
              id="clinical_director_id-field"
              style={{ ...UIUtils.SELECT_FIELD_STYLE }}
              label="Clinical Director"
              name="clinical_director_id"
              value={clinic.clinical_director_id}
              onChange={onClinicChange}
              inputElements={allPhysicians.map((physician) => (
                <MenuItem key={physician.id} value={physician.id}>
                  {PhysicianUtils.getName(physician)}
                </MenuItem>
              ))}
            />
          </div>
        </div>

        {/* Message Section */}
        <div className="clinic-section">
          <div className="section-header">
            <Icon src="message" height="20px" className="section-icon" />
            <span className="section-title">Clinic Message</span>
          </div>
          <div className="section-content">
            <TextareaAutosize
              cacheMeasurements
              name="message"
              value={clinic.message}
              onChange={onClinicChange}
            />
          </div>
        </div>

        {/* Rooms Section */}
        <div className="clinic-section rooms-section">
          <div className="section-header">
            <Icon src="door-open" height="20px" className="section-icon" />
            <span className="section-title">Rooms</span>
          </div>
          <div className="section-content">
            <Rooms rooms={clinic.rooms} onChange={onClinicChange} />
          </div>
        </div>

        {/* Hours Section */}
        <div className="clinic-section">
          <div className="section-header">
            <Icon src="clock" height="20px" className="section-icon" />
            <span className="section-title">Operating Hours</span>
          </div>
          <div className="section-content">
            <ClinicHours
              hours={clinic.hours}
              onChange={(hours) => setClinic({ ...clinic, hours })}
            />
          </div>
        </div>

        {/* Technical Configuration */}
        <Collapse title="Technical Configuration" collapsed={collapsed} onClick={toggleCollapsed}>
          <div className="clinic-section" style={{ marginTop: '10px' }}>
            <div className="section-content">
              {clinic.clinic_configuration !== undefined && (
                <ClinicConfiguration
                  configuration={clinic.clinic_configuration}
                  onChange={onConfigurationChange}
                />
              )}
            </div>
          </div>
        </Collapse>

        {/* Save Button */}
        <div className="save-button-wrapper">
          <DefaultButton
            name="Save Clinic Info"
            onClick={saveClinic}
            disabled={saveClinicDisabled}
            loading={savingClinic}
            className="blue"
          />
        </div>
      </form>
    </div>
  );
};

const Rooms = ({ rooms = [], onChange }) => {
  const [room, setRoom] = useState('');

  function changeRooms(value) {
    let event = {
      target: {
        name: 'rooms',
        value,
      },
    };
    onChange(event);
  }

  function onMoveEnd(value) {
    changeRooms(value.map(({ id }) => id));
  }

  function deleteItem(item) {
    let value = [...rooms].filter((room) => room !== item?.id);
    changeRooms(value);
  }

  function addRoom() {
    let value = [...rooms, room];
    changeRooms(value);
    setRoom('');
  }

  return (
    <>
      {rooms.length > 0 && (
        <div style={{ marginBottom: '15px' }}>
          <StackItemsView
            stack={rooms.map((room) => {
              return { id: room };
            })}
            onDeleteItem={deleteItem}
            describeItem={(room) => room.id}
            onMoveEnd={onMoveEnd}
          />
        </div>
      )}
      <div className="room-add-controls">
        <DefaultTextField
          name="room"
          label="Add Room"
          value={room}
          onChange={(event) => setRoom(event.target.value)}
          style={{ flex: 1, maxWidth: '250px' }}
        />
        <IconButton
          onClick={addRoom}
          disabled={!room}
          className="blue"
          size="small"
          style={{ width: '40px', height: '40px' }}
        >
          <Icon src="plus" className={room ? 'white' : 'grey'} showTooltip={true} alt="Add room" />
        </IconButton>
      </div>
    </>
  );
};

const ClinicConfiguration = ({ configuration = {}, onChange }) => {
  let result = [];
  for (let key in configuration) {
    if (key !== 'id') {
      if (key === 'fax_cover')
        result.push(
          <div key={key} style={{ width: '100%' }}>
            <span className="small-title">{key}</span>
            <TextareaAutosize
              cacheMeasurements
              name={key}
              value={configuration[key]}
              onChange={onChange}
            />
          </div>,
        );
      else
        result.push(
          <DefaultTextField
            key={key}
            name={key}
            label={key}
            value={configuration[key]}
            onChange={onChange}
          />,
        );
    }
  }
  return result;
};

ClinicDetails.propTypes = {
  clinic: PropTypes.object.isRequired,
};

export default ClinicDetails;
